<script setup lang="ts">
import { useElementSize } from '@vueuse/core';
import { computed, useTemplateRef } from 'vue';

import { DealCodeTag } from '@apps/main/new-desktop/modules/dealCode';
import { PriceGuaranteeBanner } from '@apps/main/new-desktop/modules/priceGuarantee';
import {
  useBrowserStore,
  useCustomerFlowStore,
  useRecipeStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';

import { AppButton, AppLinkButton, AppFlicker, AppSvgIcon } from '@libs/ui';

import { useGlobalCurrencyStore } from '../../../../core/stores/globalCurrencyStore';
import {
  useTripOverviewModalsStore,
  useTripOverviewPageStore,
  useTripSummaryStore,
} from '../../stores';

interface TripSummaryColumnFooterProps {
  totalPrice: number;
  grossPrice: number | undefined;
  totalAddonsPrice: number;
  currency: string;
  paxes: string;
  travelDates: string;
  shouldShowShareButton: boolean;
}

const props = defineProps<TripSummaryColumnFooterProps>();

interface TripSummaryColumnFooterEmits {
  (eventName: 'total-price-clicked'): void;
}

const emits = defineEmits<TripSummaryColumnFooterEmits>();

const tripOverviewModalsStore = useTripOverviewModalsStore();
const tripSummaryStore = useTripSummaryStore();
const globalCurrencyStore = useGlobalCurrencyStore();
const browserStore = useBrowserStore();
const statsigEventsStore = useStatsigEventsStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const customerFlowStore = useCustomerFlowStore();
const recipeStore = useRecipeStore();
const tripSummaryColumnFooterRef = useTemplateRef('tripSummaryColumnFooterRef');
const { width } = useElementSize(tripSummaryColumnFooterRef);
const bookButtonSizeThreshold = 480;

const bookButtonSize = computed(() =>
  width.value < bookButtonSizeThreshold ? 'medium' : 'large'
);

function openTripSummary(): void {
  if (browserStore.isLargerThan1199px) {
    statsigEventsStore.logTripOverviewTripSummaryPanelOpened();
    emits('total-price-clicked');
  }
}

function handleBookClick(): void {
  if (
    customerFlowStore.isFlightOnlyFromMarketplaceFlow &&
    !recipeStore.hotelOnlyRecipe.length &&
    tripOverviewModalsStore.accommodationUpsellModalOpenCounter === 0
  ) {
    tripOverviewModalsStore.openAccommodationUpsellModal();
  } else {
    tripSummaryStore.onBookClicked();
  }
}
</script>

<template>
  <div ref="tripSummaryColumnFooterRef" class="trip-summary-column-footer">
    <div class="banners">
      <PriceGuaranteeBanner
        @learn-more="tripOverviewModalsStore.openPriceGuaranteeModal"
      />
    </div>

    <div class="trip-details">
      <div class="trip-general-info">
        <div class="section">
          <AppSvgIcon class="icon" name="people" />
          {{ props.paxes }}
        </div>
        <div v-if="props.travelDates.length" class="section">
          <AppSvgIcon name="calendarOutlined" />
          {{ props.travelDates }}
        </div>
        <AppFlicker v-else width="spacing-96" height="spacing-20" />
      </div>

      <div class="trip-summary">
        <DealCodeTag v-if="props.grossPrice" />
        <div
          v-if="tripOverviewPageStore.anyItineraryLoaded"
          class="price-section"
        >
          <div v-if="props.grossPrice" class="gross-price">
            {{
              globalCurrencyStore.formatMoney(
                props.grossPrice + props.totalAddonsPrice,
                props.currency
              )
            }}
          </div>
          <div
            class="total-price"
            :class="{ 'with-discount': props.grossPrice }"
          >
            {{
              globalCurrencyStore.formatMoney(
                props.totalPrice + props.totalAddonsPrice,
                props.currency
              )
            }}
          </div>
        </div>
        <div v-else class="price-section-loader">
          <AppFlicker
            v-if="props.grossPrice"
            width="spacing-48"
            height="spacing-32"
          />
          <AppFlicker width="spacing-96" height="spacing-40" />
        </div>

        <div class="total-price-text">
          {{ $t('planner.total_price_for_trip') }}
        </div>
        <AppLinkButton
          v-if="browserStore.isLargerThan1199px"
          class="trip-summary-link-button"
          :text="$t('planner.show_trip_summary')"
          size="m"
          @click="openTripSummary"
        />
      </div>
    </div>

    <div class="trip-summary-actions">
      <AppButton
        type="important"
        :size="bookButtonSize"
        :wide="true"
        :state="tripSummaryStore.bookingButtonState"
        @click="handleBookClick"
      >
        {{ $t('planner.booking_proceed') }}
      </AppButton>

      <AppButton
        v-if="props.shouldShowShareButton"
        type="text"
        state="enabled"
        size="medium"
        :wide="true"
        @click="tripSummaryStore.openShareWidget"
      >
        {{ $t('planner.share_or_save_trip') }}
      </AppButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.trip-summary-column-footer {
  display: flex;
  flex-direction: column;
  container-name: tripSummaryColumnFooter;
  container-type: inline-size;

  @include for-less-or-equal-than-1199-px {
    padding: var(--spacing-16);
  }

  .banners {
    margin-bottom: var(--spacing-24);
  }

  .trip-details {
    margin-bottom: var(--spacing-16);
    display: flex;
    gap: var(--spacing-8);

    @container tripSummaryColumnFooter (max-width: 500px) {
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-24);
    }

    .trip-general-info {
      display: flex;
      flex-direction: column;
      flex: 1;
      gap: var(--spacing-12);

      @container tripSummaryColumnFooter (max-width: 500px) {
        flex-direction: row;
        gap: var(--spacing-24);
      }

      .section {
        display: flex;
        align-items: center;
        gap: var(--spacing-8);
        color: var(--color-neutral-70);
        @include font-label-m-light;

        .icon {
          color: var(--color-soft-black);
        }
      }
    }

    .trip-summary {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-end;
      gap: var(--spacing-4);

      @container tripSummaryColumnFooter (max-width: 500px) {
        align-items: center;
      }

      .price-section {
        display: flex;
        gap: var(--spacing-8);

        .gross-price {
          @include font-heading-4;
          color: var(--color-neutral-70);
          align-self: center;
          text-decoration: line-through;
        }

        .total-price {
          @include font-heading-4;
          color: var(--color-neutral-70);

          &.with-discount {
            color: var(--color-error-dark);
          }
        }
      }

      .total-price-text {
        @include font-label-s-light;
        color: var(--color-neutral-70);
        margin-bottom: var(--spacing-4);
      }

      .price-section-loader {
        display: flex;
        align-items: center;
        gap: var(--spacing-8);
      }

      .trip-summary-link-button {
        color: var(--color-neutral-70);
      }
    }
  }

  .trip-summary-actions {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-16);
  }
}
</style>
